import { computed, nextTick, ref, watch } from "vue";
import { useRouter } from "vue-router";

import { useAppStore } from "~/stores";

/* Hook to sync a reactive ref with a URL query parameter */
const useLastQueryParam = <T extends string>(paramName: string, defaultValue: T, replace: boolean = false) => {
  const router = useRouter();
  const appStore = useAppStore();

  const route = computed(() => router.currentRoute.value);

  const initialValue = computed<T>(() => (appStore.lastQueryParamMap.get(paramName) as T) ?? defaultValue);
  const paramRef = ref<T>(route.value.query[paramName] ? (route.value.query[paramName] as T) : initialValue.value);

  watch(paramRef, (newValue) => {
    appStore.lastQueryParamMap.set(paramName, newValue);
    const navigationMethod = replace ? router.replace : router.push;
    navigationMethod({ query: { ...route.value.query, [paramName]: newValue } });
  });

  watch(
    () => route.value.query,
    async (query) => {
      if (!query[paramName]) {
        const navigationMethod = replace ? router.replace : router.push;
        await nextTick();
        navigationMethod({ query: { ...query, [paramName]: initialValue.value } });
      }

      const newValue = (query[paramName] ?? initialValue.value) as T;
      paramRef.value = newValue;
      appStore.lastQueryParamMap.set(paramName, newValue);
    },
    { deep: true, immediate: true }
  );

  return paramRef;
};

export default useLastQueryParam;
